import React, { useEffect, useState } from 'react';
import { CPTCodeMultiSelect } from '@components/ui/forms/CPTCodePicker';
import EditOutlined from '@mui/icons-material/EditOutlined';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import type { PriorAuthorizationCourse, PriorAuthorizationCreateRequest } from '../types';

export type PriorAuthorizationCourseFormValues = Omit<
  PriorAuthorizationCreateRequest,
  'patient_id' | 'continues_authorization_id' | 'source'
>;

export interface PriorAuthorizationCoverageOption {
  id: string;
  label: string;
}

export interface PriorAuthorizationCourseCardProps {
  course?: PriorAuthorizationCourse;
  coverageLabel?: string;
  coverageOptions?: PriorAuthorizationCoverageOption[];
  defaultCoverageId?: string | null;
  defaultKind?: PriorAuthorizationCreateRequest['kind'];
  errorMessage?: string | null;
  isSaving?: boolean;
  mode?: 'summary' | 'create' | 'edit';
  onCancel?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  onEdit?: () => void;
  onSubmit?: (values: PriorAuthorizationCourseFormValues) => void;
  subjectLabel?: string;
}

const statusColor = (
  status: PriorAuthorizationCourse['status'],
): 'success' | 'warning' | 'error' | 'default' => {
  if (status === 'active') return 'success';
  if (status === 'expired' || status === 'exhausted') return 'warning';
  if (status === 'voided' || status === 'denied') return 'error';
  return 'default';
};

const humanize = (value: string): string => value.replaceAll('_', ' ');

function CourseForm({
  course,
  coverageOptions = [],
  defaultCoverageId,
  defaultKind,
  errorMessage,
  isSaving = false,
  mode,
  onCancel,
  onDirtyChange,
  onSubmit,
  subjectLabel = 'patient',
}: Required<Pick<PriorAuthorizationCourseCardProps, 'mode'>> &
  Pick<
    PriorAuthorizationCourseCardProps,
    | 'course'
    | 'coverageOptions'
    | 'defaultCoverageId'
    | 'defaultKind'
    | 'errorMessage'
    | 'isSaving'
    | 'onCancel'
    | 'onDirtyChange'
    | 'onSubmit'
    | 'subjectLabel'
  >) {
  const [cptCodes, setCptCodes] = useState<string[]>(course?.cpt_codes ?? []);

  useEffect(() => {
    onDirtyChange?.(false);
    return () => onDirtyChange?.(false);
  }, [onDirtyChange]);

  return (
    <Box
      component="form"
      onChange={() => onDirtyChange?.(true)}
      onSubmit={event => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const value = (name: string) => {
          const entry = form.get(name);
          return typeof entry === 'string' ? entry.trim() : '';
        };
        const authEndDate = value('auth_end_date');
        const authNumber = value('auth_number');
        const coverageId = value('coverage_id');
        onSubmit?.({
          auth_number: authNumber || null,
          authorized_quantity: Number(value('authorized_quantity')),
          quantity_unit: value('quantity_unit') as PriorAuthorizationCreateRequest['quantity_unit'],
          auth_start_date: value('auth_start_date'),
          auth_end_date: authEndDate || null,
          coverage_id: coverageId || null,
          cpt_codes: cptCodes,
          kind: value('kind') as PriorAuthorizationCreateRequest['kind'],
          status: value('status') as PriorAuthorizationCreateRequest['status'],
          ...(mode === 'create' ? { prior_visits_used: Number(value('prior_visits_used')) } : {}),
        });
      }}
    >
      <Stack spacing={2}>
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        <Alert severity="info">
          Authorization numbers are optional. Use tracking-only courses for benefit limits, self-pay
          care, or other coverage rules that do not issue a number for this{' '}
          {subjectLabel.toLowerCase()}.
        </Alert>
        <TextField
          fullWidth
          select
          name="coverage_id"
          label="Insurance coverage"
          defaultValue={course?.coverage_id ?? defaultCoverageId ?? ''}
          helperText="Choose the plan this course belongs to, or track it without insurance."
        >
          <MenuItem value="">No linked coverage · tracking only</MenuItem>
          {coverageOptions.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            fullWidth
            name="auth_number"
            label="Authorization number"
            defaultValue={course?.auth_number ?? ''}
            placeholder="Optional"
            helperText="Copied to matching claims when present"
          />
          <TextField
            required
            name="authorized_quantity"
            label="Quantity"
            type="number"
            defaultValue={course?.authorized_quantity ?? ''}
            slotProps={{ htmlInput: { min: 1 } }}
            sx={{ minWidth: 120 }}
          />
          <TextField
            select
            name="quantity_unit"
            label="Unit"
            defaultValue={course?.quantity_unit ?? 'visits'}
            sx={{ minWidth: 130 }}
          >
            {['visits', 'units', 'days', 'hours'].map(unit => (
              <MenuItem key={unit} value={unit}>
                {unit}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            required
            fullWidth
            name="auth_start_date"
            label="Starts"
            type="date"
            defaultValue={course?.auth_start_date ?? ''}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            fullWidth
            name="auth_end_date"
            label="Ends"
            type="date"
            defaultValue={course?.auth_end_date ?? ''}
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Optional for an open-ended limit"
          />
        </Stack>
        <CPTCodeMultiSelect
          required
          value={cptCodes}
          label="CPT scope"
          helperText="Claims must share at least one code to match this course"
          onChange={codes => {
            setCptCodes(codes);
            onDirtyChange?.(true);
          }}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            name="prior_visits_used"
            label="Visits before JotPsych"
            type="number"
            defaultValue={course?.prior_visits_used ?? 0}
            disabled={mode === 'edit'}
            slotProps={{ htmlInput: { min: 0 } }}
            helperText={mode === 'edit' ? 'The starting count cannot be changed' : 'Starting count'}
            sx={{ flex: 1 }}
          />
          <TextField
            select
            name="kind"
            label="Kind"
            defaultValue={course?.kind ?? defaultKind ?? 'authorization'}
            sx={{ flex: 1 }}
          >
            <MenuItem value="authorization">Authorization</MenuItem>
            <MenuItem value="benefit_limit">Benefit limit</MenuItem>
          </TextField>
          <TextField
            select
            name="status"
            label="Status"
            defaultValue={course?.status ?? 'active'}
            sx={{ flex: 1 }}
          >
            {['active', 'exhausted', 'expired', 'voided', 'denied'].map(status => (
              <MenuItem key={status} value={status}>
                {humanize(status)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={isSaving}>
            {mode === 'create' ? 'Add course' : 'Save changes'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

export const PriorAuthorizationCourseCard: React.FC<PriorAuthorizationCourseCardProps> = ({
  course,
  coverageLabel = 'No linked coverage · tracking only',
  coverageOptions,
  defaultCoverageId,
  defaultKind,
  errorMessage,
  isSaving,
  mode = 'summary',
  onCancel,
  onDirtyChange,
  onEdit,
  onSubmit,
  subjectLabel,
}) => {
  const formMode = mode === 'create' || mode === 'edit';

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start' }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <FactCheckOutlined sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 750 }}>
              {mode === 'create' ? 'Add authorization or benefit limit' : 'Course coverage'}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {course ? coverageLabel : 'Choose the coverage, window, and services.'}
            </Typography>
          </Box>
          {course && !formMode ? (
            <>
              <Chip
                label={humanize(course.status)}
                color={statusColor(course.status)}
                size="small"
                variant="outlined"
              />
              {onEdit ? (
                <Button size="small" startIcon={<EditOutlined />} onClick={onEdit}>
                  Edit
                </Button>
              ) : null}
            </>
          ) : null}
        </Stack>

        <Divider />

        {formMode ? (
          <CourseForm
            course={course}
            coverageOptions={coverageOptions}
            defaultCoverageId={defaultCoverageId}
            defaultKind={defaultKind}
            errorMessage={errorMessage}
            isSaving={isSaving}
            mode={mode}
            onCancel={onCancel}
            onDirtyChange={onDirtyChange}
            onSubmit={onSubmit}
            subjectLabel={subjectLabel}
          />
        ) : course ? (
          <Stack spacing={1.5}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1.5,
              }}
            >
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Authorization
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {course.auth_number ?? 'No auth number · tracking only'}
                </Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Approved scope
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {course.course_authorized_quantity} {course.quantity_unit} ·{' '}
                  {course.cpt_codes.join(', ')}
                </Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Window
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {course.auth_start_date} – {course.auth_end_date ?? 'No end date'}
                </Typography>
              </Box>
              <Box>
                <Typography color="text.secondary" variant="caption">
                  Source
                </Typography>
                <Typography sx={{ fontWeight: 700 }}>{humanize(course.source)}</Typography>
              </Box>
            </Box>

            <Box
              sx={{
                borderRadius: 1.5,
                bgcolor: 'backgroundColors.secondary',
                border: '1px solid',
                borderColor: 'borderColors.lightprimary',
                p: 1.5,
              }}
            >
              <Typography sx={{ fontWeight: 750 }}>
                {course.documented_label ?? 'Utilization tracking unavailable'}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {course.billed_label ??
                  `Counts are not calculated for ${course.quantity_unit}-based courses.`}
              </Typography>
            </Box>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
};
